/**
 * AddTabletDialog — enroll a shared truck tablet by QR code (OPEN-035).
 *
 * Two steps: name the tablet, then show a large code for the tablet's "Scan enrollment QR
 * code" screen to read, with when it stops working. The code is a one-time credential, so it
 * is held only in this component's state: it is dropped the moment it expires, when the
 * dialog closes, and when the dialog unmounts, and it is never logged or stored.
 *
 * The tablet joins the Tablets list once it has scanned the code, so closing the dialog
 * refreshes that list.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { QrCode } from 'src/components/ui/qr-code';
import { SHARED_DEVICES_QUERY_KEY } from './use-shared-devices';
import { enrollTablet, EnrollTabletError, type IssuedEnrollment } from './enroll-tablet';

/** Matches the API's deviceName limit (enroll.ts) and the QR schema's. */
const NAME_MAX = 80;
/** Field-use minimum touch target. */
const TARGET_PX = 56;
const QR_PX = 360;

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: `${String(TARGET_PX)}px`,
  padding: '8px 12px',
  fontSize: '16px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: '6px',
};

const targetStyle: React.CSSProperties = { minHeight: `${String(TARGET_PX)}px`, fontSize: '16px' };

/** "9:58" from a millisecond count, rounded up so it never reads 0:00 while still valid. */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

export interface AddTabletDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function AddTabletDialog({ open, onClose }: AddTabletDialogProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [name, setName] = React.useState('');
  const [issued, setIssued] = React.useState<IssuedEnrollment | null>(null);
  const [expired, setExpired] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  // Bumped on every open/close, so a code that arrives after the dialog closed is discarded.
  const generation = React.useRef(0);

  // Start clean every time the dialog opens, and drop any code when it closes.
  React.useEffect(() => {
    generation.current += 1;
    setIssued(null);
    setExpired(false);
    setError(null);
    setPending(false);
    if (open) setName('');
  }, [open]);

  // Count down while a code is on screen; drop it when it expires.
  React.useEffect(() => {
    if (issued === null) return undefined;
    const tick = (): void => {
      const now = Date.now();
      setNowMs(now);
      if (now >= issued.expiresAtMs) {
        setIssued(null);
        setExpired(true);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return (): void => window.clearInterval(id);
  }, [issued]);

  const trimmed = name.trim();
  const nameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;

  const issue = async (): Promise<void> => {
    if (!nameValid || pending) return;
    const mine = generation.current;
    setPending(true);
    setError(null);
    try {
      const result = await enrollTablet(apiOpts, trimmed);
      if (mine !== generation.current) return;
      setExpired(false);
      setIssued(result);
    } catch (err) {
      if (mine !== generation.current) return;
      setError(
        err instanceof EnrollTabletError ? err.message : 'Could not make a code. Try again.',
      );
    } finally {
      if (mine === generation.current) setPending(false);
    }
  };

  const close = (): void => {
    setIssued(null);
    void queryClient.invalidateQueries({ queryKey: SHARED_DEVICES_QUERY_KEY });
    onClose();
  };

  const expiresAtText =
    issued === null
      ? ''
      : new Date(issued.expiresAtMs).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        });

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) close();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a tablet</DialogTitle>
          <DialogDescription>
            {issued === null
              ? 'Name the tablet, then scan the code with it.'
              : 'On the tablet, open "Scan enrollment QR code" and point the camera at this code.'}
          </DialogDescription>
        </DialogHeader>

        {issued !== null ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}
          >
            <div
              style={{
                padding: '8px',
                backgroundColor: 'var(--color-paper)',
                borderRadius: '8px',
                maxWidth: '100%',
              }}
            >
              <QrCode
                value={issued.qrText}
                size={QR_PX}
                label={`Enrollment code for ${issued.deviceName}`}
              />
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{issued.deviceName}</div>
            <div
              data-testid="enroll-expiry"
              style={{
                fontSize: '15px',
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              Works once. Expires at {expiresAtText} ({formatRemaining(issued.expiresAtMs - nowMs)}{' '}
              left)
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e): void => {
              e.preventDefault();
              void issue();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {expired && (
              <p role="alert" style={{ margin: 0, color: 'var(--color-warning)' }}>
                That code expired before the tablet used it. Make a new one.
              </p>
            )}
            <div>
              <label htmlFor="add-tablet-name" style={labelStyle}>
                Tablet name
              </label>
              <input
                id="add-tablet-name"
                value={name}
                maxLength={NAME_MAX}
                autoComplete="off"
                placeholder="Truck 47 tablet"
                onChange={(e): void => setName(e.target.value)}
                style={inputStyle}
              />
              <div
                style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}
              >
                Drivers see this name on the tablet.
              </div>
            </div>
            {error !== null && (
              <p role="alert" style={{ margin: 0, color: 'var(--color-danger)' }}>
                {error}
              </p>
            )}
            <Button type="submit" disabled={!nameValid || pending} style={targetStyle}>
              {pending ? 'Making code…' : expired ? 'Make a new code' : 'Make code'}
            </Button>
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} style={targetStyle}>
            {issued === null ? 'Cancel' : 'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
